<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Update Profile: {{ patientData.full_name }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <b-tabs vertical nav-class="align-items-start" class="mt-2">
          <b-tab title="Profile" active title-item-class="mb-1">
            <template #title>
              <feather-icon icon="UserIcon" size="20" />
              Profile
            </template>

            <update-patient-profile-form 
              :patient-data="patientData"
              @refresh="$emit('refresh')"
            />
          </b-tab>

          <b-tab title="Family History" title-item-class="mb-1">
            <template #title>
              <feather-icon icon="UsersIcon" size="20" />
              Family History
            </template>

            <update-patient-family-history-form
              :patient-data="patientData"
              @refresh="$emit('refresh')"
            />
          </b-tab>

          <b-tab title="Social History" title-item-class="mb-1">
            <template #title>
              <feather-icon icon="ClockIcon" size="20" />
              Social History
            </template>

            <update-patient-social-history-form
              :patient-data="patientData"
              @refresh="$emit('refresh')"
            />
          </b-tab>
        </b-tabs>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BTabs, BTab, BOverlay, BModal 
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import UpdatePatientProfileForm from "@core/components/shared/admin/patient/UpdatePatientProfileForm.vue"
import UpdatePatientFamilyHistoryForm from "@core/components/shared/admin/patient/UpdatePatientFamilyHistoryForm.vue"
import UpdatePatientSocialHistoryForm from "@core/components/shared/admin/patient/UpdatePatientSocialHistoryForm.vue"

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  question: "",
  answer_type: 'checkbox',
  select_options: [],
  related_complaint_ids: []
}

export default {
  components: {
    BTab,
    BTabs,
    BModal,
    vSelect,
    BOverlay,

    UpdatePatientProfileForm,
    UpdatePatientFamilyHistoryForm,
    UpdatePatientSocialHistoryForm,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    isAddActive: {
      type: Boolean,
      required: true,
    },
    patientData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    modalTitle: {
      type: String,
      default: 'Record'
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",

      answerTypes: [
        {
          label: 'Checkbox',
          value: 'checkbox'
        },
        {
          label: 'Text',
          value: 'text'
        },
        {
          label: 'Select',
          value: 'select'
        }
      ],
      commonComplaints: [],
      form_data: cloneDeep(rawData),
    }
  },
  computed: {
    isUpdate(){
      return !!this.patientData
    },
    disableForm(){
      return this.patientData ? !this.can(`${this.resource}:CanUpdate`) : !this.can(`${this.resource}:CanAddNew`)
    },
  },
  watch: {
    patientData: {
      handler(data){
        if (data){
          this.form_data = data
        } else {
          this.form_data = cloneDeep(rawData)
        }
      }, 
      immediate: true,
      deep: true
    }
  },
  methods: {
    async onCreateOrUpdate(save_and_new = false){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.isUpdate && !this.can(`${this.resource}:CanAddNew`)) {
          return
        }

        if (this.isUpdate && !this.can(`${this.resource}:CanUpdate`)) {
          return
        }

        const { id, question, answer_type, select_options, related_complaint_ids } = this.form_data;
        if (this.isUpdate){
          this.$emit('update', {
            id,
            payload: {
              question, answer_type, select_options, related_complaint_ids
            }
          });
        } else {
          this.$emit('create', {
            payload: {
              question, answer_type, select_options, related_complaint_ids
            },
            save_and_new
          });
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form_data = cloneDeep(rawData)
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
